import { AuthMethods, AUTH_KEYS } from "@/lib/consts/auth";
import sessionService from "@/lib/services/session";
import { getProfileImageUrl } from "@/lib/utils/profile";
import { BootAuthResponse } from "@/lib/types";
import { getUserToken } from "@/lib/utils/user";
import { getOs } from "@/lib/utils/device";
import { FeatureFlags } from "@/lib/consts/featureFlags";
import analyticsService from "@/lib/services/analytics";
import { AnalyticsEventName } from "../consts/analytics";
import { API_ROUTES } from "@/lib/consts/api";
import { LoginResponse } from "@/lib/types/login";
import playerService from "@/lib/services/player";
import { buildFailedLoginUrl, sendLoginFailedPublisherWebhookEvents, sendPostLoginPublisherWebhookEvents } from "@/lib/utils/login";
import { getAuthErrorData } from "@/lib/utils/error";
import { setSessionAction } from "./session.actions";
import { httpClient } from "@/lib/http-client";
import { serverLogger } from "../services/serverLogger";
import { LoggerEventNames } from "../consts/logger";
import coralogixRumService from "../services/coralogixRum";
import { sessionManager } from "../session";
import { getDevicePlatform } from "../utils/platform";
import { webStorageUtil } from "../storage/webStorageUtil";
import { ELocalStorageKeys } from "../storage/enums";
import { getUtmHeaders } from "../utils/utm";
import { SESSION_STORAGE_KEYS } from "../consts/sessionStorage";

export enum LoginResult {
    SUCCESS = 'success',
    FAILED = 'failed',
}

export enum LoginResultReason {
    UNKNOWN = 'unknown',
}

export interface PerformLoginResult {
    result: LoginResult;
    failedLoginUrl?: string;
}

interface PerformLoginProps {
    localAddress?: string;
    authMethod: AuthMethods;
    userToken?: string;
    userName?: string;
    userPassword?: string;
    otp?: {
        playerCode: string;
        accessToken?: string;
    };
    jwt?: string;
}

interface PerformStoreImpersonationLoginProps {
    playerId: string;
    impersonatedPublisherId: string;
}

export const handleSuccessfulLogin = async (response: LoginResponse | undefined, authMethod: AuthMethods): Promise<PerformLoginResult> => {
    if (!response) {
        // 401/403 responses come back with `data: undefined` from the http
        // client. Treat as an auth failure rather than throwing.
        const failedLoginUrl = handleFailedLogin(undefined, authMethod);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
    if (response.errorCode === 100) {
        const failedLoginUrl = handleFailedLogin(response, authMethod);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }

    const playerData = {
        playerId: response.playerId,
        playerName: response.playerName,
        playerCountry: response.playerCountry,
        profileImageUrl: response.profileImageUrl || response.playerProfileImage,
        authMethod,
        marketingEmailConsent: response.marketingEmailConsent,
    };

    sessionService.setSessionToken(response.sessionToken);
    httpClient.setDefaultHeader('Authorization', `Bearer ${response.sessionToken}`);
    webStorageUtil.set(ELocalStorageKeys.SESSION_TOKEN, response.sessionToken);
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SESSION_REJECTED);
    } catch {
        // ignore — login still succeeds
    }
    playerService.setPlayerData(playerData);
    coralogixRumService.setPlayerContext(playerData);
    coralogixRumService.sendInfoEvent(LoggerEventNames.LOGIN_COMPLETED, {
        playerId: playerData.playerId,
        authMethod,
    });
    await setSessionAction(response.sessionToken);
    
    analyticsService.trackEvent(AnalyticsEventName.LOGIN_RESULT, {
        results: LoginResult.SUCCESS,
        country: response.playerCountry,
    });

    sendPostLoginPublisherWebhookEvents(authMethod);
    return { result: LoginResult.SUCCESS };
}

export const isLoggedInSuccessfully = (response: LoginResponse | undefined): boolean => {
    return Boolean(response && !response.errorCode && response.sessionToken);
}

export const handleFailedLogin = (error: unknown, authMethod: AuthMethods): string | undefined => {
    serverLogger.error(LoggerEventNames.LOGIN_ERROR, { error });
    const errorData = getAuthErrorData(error, authMethod);

    // The player is about to land on /failed?error=auth, which fires no
    // analytics — without this the whole failure is invisible client-side.
    coralogixRumService.sendErrorEvent(LoggerEventNames.LOGIN_FAILED, {
        authMethod,
        errorCode: errorData?.errorCode,
        errorMessage: errorData?.errorMessage,
    });

    analyticsService.trackEvent(AnalyticsEventName.LOGIN_RESULT, {
        results: LoginResult.FAILED,
        errorCode: errorData?.errorCode,
        errorMessage: errorData?.errorMessage,
        playerId: null,
    });
    sendLoginFailedPublisherWebhookEvents(errorData ?? { authMethod, errorCode: 0, errorMessage: LoginResultReason.UNKNOWN });

    if (!errorData) return undefined;
    return buildFailedLoginUrl(errorData);
}

export const handleLoginResponse = async (response: LoginResponse | undefined, props: PerformLoginProps): Promise<PerformLoginResult> => {
    if (isLoggedInSuccessfully(response)) {
        return await handleSuccessfulLogin(response, props.authMethod);
    } else {
        const failedLoginUrl = handleFailedLogin(response, props.authMethod);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
}

export const prepareLoginData = async (props: PerformLoginProps, authBoot: BootAuthResponse) => {
    const { localAddress, authMethod, userToken } = props;
    const profileImageUrl = await getProfileImageUrl(authMethod, userToken ?? '');
    const sessionId = sessionManager.getOrCreateSessionId();
    const isIntegrationProfilesEnabled = authBoot.integration.isProfilesEnabled;
    const shouldSendOs = authBoot.featureFlags[FeatureFlags.STORE_SEND_OS_AUTH_LOGIN];

    return {
        ...(shouldSendOs ? { os: getOs() } : {}),
        platform: getDevicePlatform(),
        ...props,
        localAddress,
        sessionId,
        profileImageUrl,
        ...(authMethod !== AuthMethods.OTP && authMethod !== AuthMethods.USERNAME ? { userToken: getUserToken(authBoot, authMethod, userToken ?? '') } : {}),
        isIntegrationProfilesEnabled,
        publisherId: authBoot.storeTheme.publisherId,
    }
}

export const performLogin = async (props: PerformLoginProps, authBoot: BootAuthResponse): Promise<PerformLoginResult> => {
    analyticsService.trackEvent(AnalyticsEventName.LOGIN_ATTEMPTED, { authMethod: props.authMethod });
    // Pairs with LOGIN_COMPLETED: an attempt with no completion means the
    // request went out but the browser never processed the response.
    coralogixRumService.sendInfoEvent(LoggerEventNames.LOGIN_ATTEMPTED, { authMethod: props.authMethod });
    const loginData = await prepareLoginData(props, authBoot);
    const utmHeaders = getUtmHeaders();
    try {
        const response = await httpClient.post<LoginResponse>(API_ROUTES.LOGIN, loginData, { headers: utmHeaders });
        return await handleLoginResponse(response.data, props);
    } catch (error) {
        const failedLoginUrl = handleFailedLogin(error, props.authMethod);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
}

export const performStoreImpersonationLogin = async (
    props: PerformStoreImpersonationLoginProps,
    authBoot: BootAuthResponse,
): Promise<PerformLoginResult> => {
    const authMethod = AuthMethods.STORE_IMPERSONATION;
    analyticsService.trackEvent(AnalyticsEventName.LOGIN_ATTEMPTED, { authMethod });
    coralogixRumService.sendInfoEvent(LoggerEventNames.LOGIN_ATTEMPTED, { authMethod });

    const loginData = {
        publisherId: authBoot.storeTheme.publisherId,
        playerId: props.playerId,
        impersonatedPublisherId: props.impersonatedPublisherId,
    };
    const utmHeaders = getUtmHeaders();

    try {
        const response = await httpClient.post<LoginResponse>(
            API_ROUTES.STORE_IMPERSONATION_LOGIN,
            loginData,
            { headers: utmHeaders },
        );
        return await handleLoginResponse(response.data, { authMethod });
    } catch (error) {
        const failedLoginUrl = handleFailedLogin(error, authMethod);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
}

export const performReconnect = async ({ sessionId }: { sessionId: string }): Promise<PerformLoginResult> => {
    const utmHeaders = getUtmHeaders();
    try {
        const response = await httpClient.post<LoginResponse>(API_ROUTES.RECONNECT, { sessionId }, { headers: utmHeaders });
        return await handleLoginResponse(response.data, { authMethod: AuthMethods.RECONNECT });
    } catch (error) {
        const failedLoginUrl = handleFailedLogin(error, AuthMethods.RECONNECT);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
}

export const performExternalLogin = async ({ sessionId, jwtToken, publisherId }: { sessionId: string, jwtToken: string, publisherId: string }): Promise<PerformLoginResult> => {
    const utmHeaders = getUtmHeaders();
    try {
        const data = { sessionId, jwt: jwtToken, publisherId };
        const response = await httpClient.post<LoginResponse>(API_ROUTES.EXTERNAL_LOGIN, data, { headers: utmHeaders });
        return await handleLoginResponse(response.data, { authMethod: AuthMethods.EXTERNAL });
    } catch (error) {
        const failedLoginUrl = handleFailedLogin(error, AuthMethods.EXTERNAL);
        return { result: LoginResult.FAILED, failedLoginUrl };
    }
}